import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { UntilDestroy } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter, finalize, take, tap } from 'rxjs/operators';

import { MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import { DialogBase, TiimeSnackbarService } from 'tiime-material';

import { PcgEntryApiService } from '@api-services/pcg-entry-api.service';
import { PcgEntryForm } from '@forms/pcg-entry-form';
import { AppStoreState } from '@interfaces/app-store-state';
import { AccountingConfig } from '@models/accounting-config';
import { Company } from '@models/company';
import { PcgEntry } from '@models/pcg-entry';
import { accountingConfigSelector } from '@modules/company/company-core/store/accounting-config/accounting-config-selector';
import { companySelector } from '@modules/company/company-core/store/company/company-selector';
import { ConsistencyChecksService } from '@modules/company/company-core/store/consistency-checks/consistency-checks-service';
import * as PcgEntriesActions from '@modules/company/company-core/store/pcg-entries/pcg-entries-actions';

import { CreatePcgEntryDialogData } from './create-pcg-entry-dialog-data';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-create-pcg-entry-dialog',
  templateUrl: './create-pcg-entry-dialog.component.html',
  styleUrls: ['./create-pcg-entry-dialog.component.scss']
})
export class CreatePcgEntryDialogComponent extends DialogBase<CreatePcgEntryDialogComponent> implements OnInit {
  company: Company;
  accountingConfig: AccountingConfig;
  disableCentralizer = false;
  pcgEntries: PcgEntry[];
  pcgEntryForm: PcgEntryForm;
  title = 'Ajouter un compte';

  private subscription = new Subscription();

  constructor(
    dialogRef: MatDialogRef<CreatePcgEntryDialogComponent>,
    private pcgEntryApiService: PcgEntryApiService,
    private snackbarService: TiimeSnackbarService,
    private store: Store<AppStoreState>,
    private consistencyChecksService: ConsistencyChecksService,
    @Inject(MAT_DIALOG_DATA)
    createPcgEntryDialogData: CreatePcgEntryDialogData
  ) {
    super(dialogRef);
    if (createPcgEntryDialogData.withCentralizer) {
      this.title = 'Ajouter un compte centralisateur';
    }
    this.initPcgEntries(createPcgEntryDialogData);
    this.initPcgEntryForm(createPcgEntryDialogData);
  }

  ngOnInit(): void {
    this.initCompany();
    this.initAccountingConfig();
  }

  @MarkFormAsTouchedIfInvalid('pcgEntryForm')
  submitPcgEntryForm(): void {
    const pcgEntry = this.pcgEntryForm.toPcgEntry();
    this.pcgEntryForm.disable();
    this.isLoading.set(true);

    this.pcgEntryApiService
      .createPcgEntry(pcgEntry)
      .pipe(
        tap(() => this.store.dispatch(PcgEntriesActions.load())),
        tap((createdPcgEntry: PcgEntry) => this.dialogRef.close(createdPcgEntry)),
        tap(() => this.consistencyChecksService.expireForKey('pcgEntries')),
        tap(() => this.snackbarService.openSuccess('Le compte a bien été créé')),
        finalize(() => {
          this.pcgEntryForm.enable();
          if (pcgEntry.centralizer) {
            this.pcgEntryForm.centralizer.disable();
          }
          this.isLoading.set(false);
        })
      )
      .subscribe();
  }

  private initCompany(): void {
    this.subscription.add(
      this.store
        .pipe(
          select(companySelector),
          take(1),
          tap(company => (this.company = company))
        )
        .subscribe()
    );
  }

  private initAccountingConfig(): void {
    this.subscription.add(
      this.store
        .pipe(
          select(accountingConfigSelector),
          filter(accountingConfig => !!accountingConfig),
          take(1),
          tap(accountingConfig => (this.accountingConfig = accountingConfig))
        )
        .subscribe()
    );
  }

  private initPcgEntries(createPcgEntryDialogData: CreatePcgEntryDialogData): void {
    if (createPcgEntryDialogData.pcgEntries) {
      this.pcgEntries = createPcgEntryDialogData.pcgEntries;
    } else {
      this.isLoading.set(true);
      this.pcgEntryApiService
        .getPcgEntries()
        .pipe(
          tap((pcgEntries: PcgEntry[]) => (this.pcgEntries = pcgEntries)),
          finalize(() => this.isLoading.set(false))
        )
        .subscribe();
    }
  }

  private initPcgEntryForm(createPcgEntryDialogData: CreatePcgEntryDialogData): void {
    this.pcgEntryForm = new PcgEntryForm(
      createPcgEntryDialogData.additionalPattern,
      createPcgEntryDialogData.additionalPatternErrorMessage
    );
    this.pcgEntryForm.accountNumber.setValue(createPcgEntryDialogData.accountNumber);
    this.pcgEntryForm.accountNumber.completeAccountNumber();
    if (createPcgEntryDialogData.withCentralizer) {
      this.pcgEntryForm.centralizer.setValue(true);
      this.disableCentralizer = true;
    } else {
      this.pcgEntryForm.centralizer.setValue(false);
    }
  }
}
